import { NormalizedDropdownItemType } from '@entur/dropdown';
import { OrganisationList, PartnerDtoV2, PartnerRptV2 } from 'types';
import { PartnerRptV2WithId, PartnerRptV2WithOrgRef } from './types';

interface PartnerValueMap {
  id: number;
  organisationRef: string;
}

export function getPartnerLabel(partner?: PartnerDtoV2 | PartnerRptV2 | null, showStatus = false): string {
  if (!partner) return 'Ukjent';
  if (!partner.name && partner.organisationRef) return `Ukjent (${partner.organisationRef})`;
  if (!partner.name || !partner.organisationRef) return `Ukjent (id: ${partner.id})`;
  const label = `${partner.name} (${partner.organisationRef})`;
  if (partner.status === 2 && showStatus) {
    return `${label} 🟡`;
  }
  if (partner.status === 3 && showStatus) {
    return `${label} 🔴`;
  }
  return label;
}

const filterDropdownItemsById = (
  items: Array<PartnerDtoV2 | PartnerRptV2>,
  selectedIds?: number[]
): PartnerRptV2WithId[] => {
  return items.filter(
    (item) => Boolean(item.id) && (item.status !== 3 || selectedIds?.includes(Number(item.id)))
  ) as PartnerRptV2WithId[];
};

const filterDropdownItemsByOrgRef = (
  items: Array<PartnerDtoV2 | PartnerRptV2>,
  selectedOrgRefs?: string[]
): PartnerRptV2WithOrgRef[] => {
  return items.filter(
    (item) => Boolean(item.organisationRef) && (item.status !== 3 || selectedOrgRefs?.includes(item.organisationRef!))
  ) as PartnerRptV2WithOrgRef[];
};

const getPartnerDropdownItemsById = (
  items: Array<PartnerDtoV2 | PartnerRptV2>,
  selectedIds?: number[],
  showStatus = false
): NormalizedDropdownItemType<number>[] => {
  const filteredItems = filterDropdownItemsById(items, selectedIds);

  // Find all unknown items and add them to the list
  const missingIds = selectedIds?.filter((id) => !filteredItems.some((item) => item.id === id)) ?? [];
  const missingItems = missingIds.map((id) => ({ id }));
  const allItems = [...filteredItems, ...missingItems];

  return allItems.map((item) => ({
    label: getPartnerLabel(item, showStatus),
    value: Number(item.id),
  }));
};

const getPartnerDropdownItemsByOrgRef = (
  items: Array<PartnerDtoV2 | PartnerRptV2>,
  selectedOrgRefs?: string[],
  showStatus = false
): NormalizedDropdownItemType<string>[] => {
  const filteredItems = filterDropdownItemsByOrgRef(items, selectedOrgRefs);

  // Find all unknown items and add them to the list
  const missingOrgRefs =
    selectedOrgRefs?.filter((orgRef) => !filteredItems.some((item) => item.organisationRef === orgRef)) ?? [];
  const missingItems = missingOrgRefs.map((orgRef) => ({ organisationRef: orgRef }));
  const allItems = [...filteredItems, ...missingItems];

  return allItems.map((item) => ({
    label: getPartnerLabel(item, showStatus),
    value: item.organisationRef!,
  }));
};

export function getPartnerDropdownItems<T extends keyof PartnerValueMap = 'id'>(
  partners: Array<PartnerDtoV2 | PartnerRptV2> | null | undefined,
  valueField?: T,
  selectedIds?: PartnerValueMap[T][],
  showStatus = false
): NormalizedDropdownItemType<PartnerValueMap[T]>[] {
  const field = valueField ?? 'id';
  if (!partners) return [];

  if (field === 'id') {
    const items = getPartnerDropdownItemsById(partners, selectedIds as number[], showStatus);
    return items as NormalizedDropdownItemType<PartnerValueMap[T]>[];
  }

  const items = getPartnerDropdownItemsByOrgRef(partners, selectedIds as string[], showStatus);
  return items as NormalizedDropdownItemType<PartnerValueMap[T]>[];
}

export const getOrganisationItems = (
  organisationList: OrganisationList | undefined
): NormalizedDropdownItemType<string>[] => {
  return (
    organisationList?.organisations?.reduce<NormalizedDropdownItemType<string>[]>((acc, org) => {
      if (org.organisationId !== undefined) {
        acc.push({
          label: `${org.organisationId} ${org.tradingName}`,
          value: org.organisationId.toString(),
        });
      }
      return acc;
    }, []) || []
  );
};
